import { Component, Emit, Prop, PropSync, Watch } from 'vue-property-decorator'
import GridHeader from '@/components/GridHeader.vue'
import GridDisplayOptions from '@/components/GridDisplayOptions.vue'
import VtGrid from '@/components/VtGrid/VtGrid.vue'
import BaseCrudServerSideComponent from '@/shared/resources/base-crud-server-side.component'
import { BaseCrud } from '@/shared/models/cruds.models'
import {
    AppliedBillableConceptChangedEvent,
    BillingModalDeliveryNotesCrudGridState,
    initialBillingModalDeliveryNotesCrudGrid
} from '@/modules/billing-module/components/modal/delivery-notes/crud/components/grid/billing-modal-delivery-notes-crud-grid.model'
import { AppliedBillableConceptResponse } from '@/api/services/billing/applied-billable-concept/billing-applied-billable-concept.dto'
import { BillingModalDeliveryNotesCrudGridDatasource } from '@/modules/billing-module/components/modal/delivery-notes/crud/components/grid/config/billing-modal-delivery-notes-crud-grid.datasource'
import {
    CellValueChangedEvent,
    GetContextMenuItemsParams,
    GridReadyEvent,
    MenuItemDef
} from '@ag-grid-enterprise/all-modules'
import { billingCommonDeliveryNoteService } from '@/api/services/billing/common-delivery-note/billing-common-delivery-note.service'
import {
    BillableDeliveryNoteResponse,
    BillableDeliveryNoteStatus
} from '@/api/services/billing/common-delivery-note/billing-common-delivery-note.dto'
import { PromiseUtils } from '@/shared/util/promise-utils'
import { ToastUtils } from '@/shared/util/toast-utils'
import { store } from '@/store/store'
import { Nullable, StrictNullable } from '@/shared/types/app.types'
import { AgGridRowModelType } from '@/shared/models/ag-grid.model'
import { CommonExportType, ExportCommonParams } from '@/api/resources/common/shared/models/common.models'
import { AxiosResponse } from 'axios'
import { EntityIdentifiersRequest } from '@/shared/models/http/http.model'
import { ReportGridRequest } from '@/api/services/common.dto'
import { GridUtils } from '@/shared/util/grid-utils'
import { ModalUtils } from '@/shared/util/modal-utils'
import { FileUtils } from '@/shared/util/file-utils'

@Component({
    components: {
        GridHeader,
        GridDisplayOptions,
        VtGrid
    }
})
export default class BillingModalDeliveryNotesCrudGrid extends BaseCrudServerSideComponent<AppliedBillableConceptResponse> implements BaseCrud {
    @PropSync('appliedBillableConcepts', { type: Array, default: () => [] })
    syncedAppliedBillableConcepts!: AppliedBillableConceptResponse[]

    @Prop({ type: Object })
    readonly deliveryNote!: Nullable<BillableDeliveryNoteResponse>

    @Emit()
    conceptsCountChanged(count: number): number {
        return count
    }
    @Emit()
    appliedBillableConceptChanged({ data, oldValue, newValue, colDef }: CellValueChangedEvent): AppliedBillableConceptChangedEvent {
        return { field: colDef.field!, data, oldValue, newValue }
    }
    @Emit()
    appliedBillableConceptsDeleted(): void {
        // emits an event
    }

    @Watch('_rowCount')
    private _onRowCountChanged(): void {
        this.conceptsCountChanged(this._rowCount)
    }

    state: BillingModalDeliveryNotesCrudGridState
    constructor() {
        super()
        this.state = initialBillingModalDeliveryNotesCrudGrid()
    }

    /// GRID COMPUTED ///

    private _canRemoveConcepts(appliedBillableConcepts: AppliedBillableConceptResponse[]): boolean {
        return !!appliedBillableConcepts.length &&
            (
                !this.deliveryNote ||
                this.deliveryNote.status.code === BillableDeliveryNoteStatus.GENERATED ||
                (
                    this.deliveryNote.status.code === BillableDeliveryNoteStatus.ISSUED &&
                    appliedBillableConcepts.every(appliedBillableConcept => !appliedBillableConcept.autoInvoiceSystemOperationCode)
                )
            )
    }
    get canRemoveSelectedConcepts(): boolean {
        return this._canRemoveConcepts(this.state.selectedRows)
    }

    get visibleRemoveConcepts(): boolean {
        return this.deliveryNote?.status.code !== BillableDeliveryNoteStatus.VERIFIED
    }
    get visibleOpenDeliveryNoteReport(): boolean {
        return !!this.deliveryNote
    }
    get visibleActions(): boolean {
        return this.visibleRemoveConcepts ||
            this.visibleOpenDeliveryNoteReport
    }

    private get _rowCount(): number {
        if (this.deliveryNote) {
            return this.state.gridApi?.paginationGetRowCount() ?? 0
        }
        return this.state.rowData?.length ?? 0
    }
    get gridTitle(): string {
        return `${this._rowCount} ${this.$tc('accounting.field.concept', this._rowCount)}`
    }
    get gridSubtitle(): string {
        if (this.state.selectedRows.length) {
            const choice = this.state.selectedRows.length > 1 ? 2 : 1
            return `${this.state.selectedRows.length} ${this.$tc('global.selectedElements', choice)}`
        }
        return ''
    }
    get rowModel(): string {
        return this.state.gridOptions.rowModelType ?? ''
    }

    /// VUE HOOKS ///

    created(): void {
        this.state.gridOptions.onCellValueChanged = this.appliedBillableConceptChanged
    }

    mounted(): void {
        this.state.rowData = []
        this.state.datasource = new BillingModalDeliveryNotesCrudGridDatasource(this.deliveryNote?.id)
        if (this.deliveryNote) {
            this.state.gridOptions.rowModelType = AgGridRowModelType.SERVER_SIDE
        } else {
            this.state.gridOptions.rowModelType = AgGridRowModelType.CLIENT_SIDE
            this.state.rowData = this.syncedAppliedBillableConcepts
        }
    }

    /// AG GRID HOOKS ///

    private _setRowData(rowData: AppliedBillableConceptResponse[]): void {
        if (this.state.gridApi) {
            this.state.gridApi.setRowData(rowData)
            this.state.gridApi.selectAll()
        }
    }

    onGridReady(event: GridReadyEvent): void {
        this.state.gridApi = event.api
        this.state.columnApi = event.columnApi

        if (
            this.state.gridOptions.rowModelType === AgGridRowModelType.SERVER_SIDE &&
            this.state.datasource
        ) {
            this.state.gridApi.setServerSideDatasource(this.state.datasource)
        } else {
            this._setRowData(this.state.rowData ?? [])
        }
    }

    getContextMenuItems(params: GetContextMenuItemsParams): MenuItemDef[] {
        const menuItems: MenuItemDef[] = []
        if (params.node) {
            const selectedNode: AppliedBillableConceptResponse = params.node.data
            if (this._canRemoveConcepts([selectedNode])) {
                menuItems.push({
                    name: this.$tc('accounting.action.deleteConcept'),
                    action: () => this.removeSelectedConcepts([selectedNode.id])
                })
            }
        }
        return menuItems
    }

    /// SERVICE CALLS ///

    private async _removeConcepts(ids: number[]): Promise<boolean> {
        if (ids.length) {
            if (this.deliveryNote) {
                const response =
                    await PromiseUtils.toCallSecure(
                        billingCommonDeliveryNoteService.removeConcepts(
                            store.state.labId,
                            this.deliveryNote.id,
                            ids))
                if (response !== null) {
                    this.reloadData()
                    return true
                }
            } else {
                this.state.rowData =
                    this.state.rowData?.filter(appliedBillableConcept => !ids.includes(appliedBillableConcept.id)) ?? []
                this._setRowData(this.state.rowData)
                this.syncedAppliedBillableConcepts = this.state.rowData
                return true
            }
        }
        return false
    }

    private _downloadDeliveryNoteReport(billableDeliveryNoteId: number): Promise<StrictNullable<Blob>> {
        return PromiseUtils.toCallSecure(
            billingCommonDeliveryNoteService.getReport(store.state.labId, billableDeliveryNoteId, false),
            error => ModalUtils.showErrorModalFromErrorResponse(error, this.$tc('print.modals.errorReport')))
    }

    /// GRID OPERATIONS ///

    onClearFilter(): void {
        this.state.gridApi?.setFilterModel(null)
    }

    async openDeliveryNoteReport(): Promise<void> {
        if (this.deliveryNote) {
            const blob = await this._downloadDeliveryNoteReport(this.deliveryNote.id)
            if (blob !== null) {
                FileUtils.openFile({ blob })
            }
        }
    }

    async exportReport(exportType: CommonExportType): Promise<AxiosResponse<Blob> | null> {
        const columns = this.getColumnsToExport()
        if (columns && this.state.gridApi && this.state.datasource?.exporter) {
            const entityIdentifiersRequest: EntityIdentifiersRequest = {
                entityIdentifiers: this.state.selectedRows.map(selectedRow => selectedRow.id)
            }
            const request: ReportGridRequest = {
                sectionLabel: this.gridTitle,
                columns,
                entityIdentifiersRequest,
                filterModel: this.state.gridApi.getFilterModel()
            }
            const params: ExportCommonParams = {
                sort: GridUtils.getSortValue(this.state.gridApi.getSortModel()),
                exportType
            }
            return this.state.datasource.exporter(request, params)
        }
        return null
    }

    async removeSelectedConcepts(
        ids = this.state.selectedRows.map(selectedRow => selectedRow.id)
    ): Promise<void> {
        if (await this._removeConcepts(ids)) {
            ToastUtils.showToastSuccess(
                this.$tc('accounting.deliveryNote.removeConcepts', ids.length, { elements: ids.length }))
            this.appliedBillableConceptsDeleted() // emits an event
        }
    }
}
